import { lazy } from 'react';

import { label, type FeatureModule } from '@/platform/app/contracts';

import './analytics/backoffice.events';
import { backofficeI18nBundle } from './i18n';

const BackofficeNavigation = lazy(() =>
  import('./presentation/BackofficeNavigation').then((module) => ({ default: module.BackofficeNavigation })),
);
const BackofficeCardsNavigation = lazy(() =>
  import('./presentation/BackofficeCardsNavigation').then((module) => ({
    default: module.BackofficeCardsNavigation,
  })),
);

/** Sidebar group the console's items attach to; each app declares it in `navSections`. */
export const BACKOFFICE_NAV_SECTION = 'backoffice';

/** Where staff land (`AppDefinition.routes.internalLanding`). */
export const BACKOFFICE_LANDING = '/app/backoffice';

/**
 * The staff console: overview, organizations and users of the staff member's platform, with audited
 * "sign in as" and promotion. Mounted only for internal users (`audience: 'internal'`); customers never
 * load a byte of it. Backend contract: backend/docs/architecture/internal-users.md.
 */
export const backofficeModule: FeatureModule = {
  id: 'backoffice',
  audience: 'internal',
  routes: [{ path: 'backoffice/*', to: BACKOFFICE_LANDING, Component: BackofficeNavigation }],
  nav: [
    { sectionId: BACKOFFICE_NAV_SECTION, order: 10, to: BACKOFFICE_LANDING, label: label('backoffice', 'nav_overview') },
    {
      sectionId: BACKOFFICE_NAV_SECTION,
      order: 20,
      to: '/app/backoffice/organizations',
      label: label('backoffice', 'nav_organizations'),
    },
    { sectionId: BACKOFFICE_NAV_SECTION, order: 30, to: '/app/backoffice/users', label: label('backoffice', 'nav_users') },
  ],
  i18n: backofficeI18nBundle,
};

/**
 * Card support tooling on top of the console: reconciliation cases and the provider audit trail. Monokoro
 * only — the backend admits nobody else (`InternalAuth('monokoro')`). Ship it together with
 * `backofficeModule`, which owns the namespace and the nav section.
 */
export const backofficeCardsModule: FeatureModule = {
  id: 'backoffice-cards',
  audience: 'internal',
  routes: [{ path: 'backoffice/cards/*', to: '/app/backoffice/cards', Component: BackofficeCardsNavigation }],
  nav: [
    {
      sectionId: BACKOFFICE_NAV_SECTION,
      order: 40,
      to: '/app/backoffice/cards',
      label: label('backoffice', 'nav_cards_cases'),
    },
    {
      sectionId: BACKOFFICE_NAV_SECTION,
      order: 50,
      to: '/app/backoffice/cards/provider-calls',
      label: label('backoffice', 'nav_cards_calls'),
    },
  ],
};
